import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import { switchConsequence, switchedToast } from './notification-state.ts';

/**
 * Switch an automatic player notification on or off — the control the panel
 * beside it said did not exist.
 *
 * `platform.notification_setting.enabled` had no writer, and the panel was
 * labelled `read-only` saying so. It has a writer now — and STILL NO READER: no
 * mailer call, job or player route consults these rows (fix round 1 of the
 * 30 Oct 2026 walk, I1). So the dialog records the move, with its reason, and
 * says what really happens for the row (`switchConsequence`): today it changes
 * nothing a player receives. Whether the switches should work or go is the
 * owner's decision.
 *
 * A DIALOG RATHER THAN A SWITCH IN THE ROW, on purpose. A bare switch records a
 * change with no reason, and "why is the withdrawal notice off?" is the only
 * question anybody asks of this table a month later. The reason code is a closed
 * set so it can be counted, and the note is what the next operator reads.
 *
 * The MANDATORY row is a control that is rendered and refuses, not one that is
 * hidden: the licence requires a player to be told when a restriction lands on
 * their account, so the panel states that in visible text and the button to
 * switch it off is not offered at all. A switch that moved and was then rejected
 * by the database would teach an operator that the screen is broken.
 */
export interface NotificationSetting {
  key: string;
  label: string;
  description: string;
  mandatory: boolean;
  enabled: boolean;
}

/**
 * Matches `notificationSettingReason` in @dayon/contracts exactly — the route
 * parses against that enum, so a code invented here would come back as a field
 * error the operator cannot act on.
 *
 * ONE list for both directions, as the contract has it: a template is corrected
 * and the notice goes back on under the same code that took it off. The labels
 * are written to read either way for that reason.
 */
const REASONS = [
  { code: 'TEMPLATE_INCORRECT', label: 'The copy is wrong' },
  { code: 'CHANNEL_OUTAGE', label: 'The channel is down' },
  { code: 'EXCESSIVE_VOLUME', label: 'Too many of these are going out' },
  { code: 'REGULATORY_REQUIREMENT', label: 'Regulatory requirement' },
  { code: 'ISSUE_RESOLVED', label: 'The problem is fixed' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
] as const;

export function NotificationSwitchDialog({
  setting,
  onClose,
  onDone,
}: {
  setting: NotificationSetting | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [reasonCode, setReasonCode] = useState<string>(REASONS[0].code);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /* The state being ASKED FOR is the opposite of the current one, and it is read
     off the row rather than held as its own piece of state — so a stale dialog
     left open while somebody else switched the row cannot ask for the state it
     is already in. The route refuses that anyway, by name. */
  const turningOn = setting !== null && !setting.enabled;

  useEffect(() => {
    /* Defaulted to the code that fits the direction: turning something back on
       is almost always because the problem is fixed, and turning it off is
       almost always the copy. A default nobody re-reads is how a log fills up
       with one reason code. */
    setReasonCode(setting && !setting.enabled ? 'ISSUE_RESOLVED' : 'TEMPLATE_INCORRECT');
    setNote('');
    setFields({});
  }, [setting]);

  const close = () => {
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (!setting) return;
    const trimmed = note.trim();
    if (trimmed.length < 10) {
      setFields({ note: 'Say why this is changing — at least 10 characters.' });
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<{ label: string; enabled: boolean }>(
        `/api/platform/notifications/${encodeURIComponent(setting.key)}`,
        { enabled: turningOn, reasonCode, note: trimmed },
      );
      /* What was recorded — not a change in what players receive, which nothing reads. */
      toast(switchedToast(result.label, result.enabled));
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: `“${setting.label}” was not changed`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={setting !== null}
      onClose={close}
      title={
        setting
          ? `${turningOn ? 'Switch on' : 'Switch off'} “${setting.label}”?`
          : 'Automatic notification'
      }
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={busy || note.trim().length < 10}
            onClick={() => void submit()}
          >
            {turningOn ? 'Switch it on' : 'Switch it off'}
          </Button>
        </>
      }
    >
      {setting && (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name={turningOn ? 'bell' : 'bell-slash'} />
            <div>
              {/* It said players would (not) be told; nothing reads the switch, so what it
                  says is what the move records and what really happens for this row. */}
              {switchConsequence(setting.key, turningOn)}
            </div>
          </div>

          <Field
            label="Reason"
            error={fields['reasonCode']}
            hint="A closed set, so a report can count how often a notice was silenced and on what grounds. The sentence below rides with it."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
              >
                {REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            error={fields['note']}
            hint="At least 10 characters. A code on its own reads as a form number; this is what the next operator reads beside the switch."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  turningOn
                    ? 'Template corrected and checked against a test send.'
                    : 'Wrong amount in the Filipino copy — off until it is corrected.'
                }
              />
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              This is recorded twice in one transaction: once against the setting, so the panel can
              say who last changed it and why, and once in the platform audit trail. Neither row can
              be edited or removed afterwards.
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
